// ─────────────────────────────────────────────────────────────────────────────
// Turning a list of messages into something that reads like a conversation
// ─────────────────────────────────────────────────────────────────────────────
// The thread used to render one row per message, each with its own avatar,
// its own name and its own timestamp. Six lines typed by one person in one
// minute therefore looked like six different people arriving — the eye has
// to read the names to find out it is all the same voice, which is exactly
// the work a layout is supposed to save it.
//
// So: consecutive messages from the same person inside a few minutes are one
// block with one header, the date gets said once at the top of each day, and
// the point you had read up to gets a line across it.
//
// All of it is arithmetic over timestamps, which is why it lives here and
// not inside a component. A rule you cannot test is a rule that quietly
// stops being true.
// ─────────────────────────────────────────────────────────────────────────────

// Long enough that a burst of typing stays one block; short enough that
// picking the thread back up after lunch starts a new one with a fresh
// timestamp on it.
export const GROUP_WINDOW_MS = 5 * 60 * 1000;

function time(value) {
  const when = new Date(value);
  return Number.isNaN(when.getTime()) ? null : when;
}

export function dayKey(value) {
  const when = time(value);
  if (!when) return '';
  // Local day, not UTC: a message sent at 7pm Central belongs to that
  // evening, not to the next morning.
  return `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(2, '0')}-${String(when.getDate()).padStart(2, '0')}`;
}

export function dayLabel(value, now = new Date()) {
  const when = time(value);
  if (!when) return '';
  const today = dayKey(now);
  const yesterday = dayKey(new Date(now.getTime() - 86400000));
  const key = dayKey(when);
  if (key === today) return 'Today';
  if (key === yesterday) return 'Yesterday';
  const sameYear = when.getFullYear() === new Date(now).getFullYear();
  return when.toLocaleDateString([], sameYear
    ? { weekday: 'short', month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

export function clockLabel(value) {
  const when = time(value);
  if (!when) return '';
  return when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * A stable colour per person.
 *
 * Grey initials on grey squares make everybody look the same, which is the
 * one thing an avatar exists not to do. The hue is derived from the id, so
 * a person is the same colour in every thread and on every machine without
 * anything being stored.
 */
const TONES = [
  { bg: '#dbeafe', fg: '#1e40af' },
  { bg: '#dcfce7', fg: '#166534' },
  { bg: '#fef3c7', fg: '#92400e' },
  { bg: '#fae8ff', fg: '#86198f' },
  { bg: '#ffe4e6', fg: '#9f1239' },
  { bg: '#ccfbf1', fg: '#115e59' },
  { bg: '#e0e7ff', fg: '#3730a3' },
  { bg: '#ffedd5', fg: '#9a3412' },
];

export function avatarTone(seed) {
  const text = String(seed || '');
  if (!text) return TONES[0];
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) - hash + text.charCodeAt(i)) | 0;
  }
  return TONES[Math.abs(hash) % TONES.length];
}

export function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

/**
 * The thread as rows to render.
 *
 * Returns a flat list so the component maps once and makes no decisions of
 * its own: 'day' separators, one 'unread' rule, and 'message' rows that
 * already know whether they open a block and whether they close one.
 *
 * `lastSeen` is the ISO stamp of the viewer's last look. The divider is
 * placed before the first message NEWER than it — and never before the
 * first message in the thread, because a rule above everything says
 * nothing except that you have not been here before.
 */
export function buildThreadRows(messages, { lastSeen = '', currentUserId = '', now = new Date() } = {}) {
  const list = (messages || []).filter(Boolean);
  const seenAt = lastSeen ? time(lastSeen) : null;
  const rows = [];
  let unreadPlaced = false;
  let previous = null;

  list.forEach((message, index) => {
    const createdAt = time(message.createdAt);
    const previousAt = previous ? time(previous.createdAt) : null;

    const newDay = !previous || dayKey(message.createdAt) !== dayKey(previous.createdAt);
    if (newDay) {
      rows.push({ kind: 'day', key: `day-${dayKey(message.createdAt)}-${index}`, label: dayLabel(message.createdAt, now) });
    }

    // Your own arrival is not news to you, so the divider skips over
    // anything you wrote — otherwise posting a message and coming back
    // puts an "unread" line above your own words.
    const isMine = !!currentUserId && message.authorId === currentUserId;
    if (
      !unreadPlaced && seenAt && createdAt && index > 0
      && createdAt.getTime() > seenAt.getTime() && !isMine
    ) {
      unreadPlaced = true;
      rows.push({ kind: 'unread', key: `unread-${message.id}` });
    }

    const sameAuthor = !!previous && previous.authorId === message.authorId;
    const closeInTime = !!(createdAt && previousAt)
      && (createdAt.getTime() - previousAt.getTime()) <= GROUP_WINDOW_MS;
    // A day rule, or the unread rule, always restarts the block: a header
    // under a separator and none above it reads as a missing name.
    const brokenBySeparator = newDay || rows[rows.length - 1]?.kind === 'unread';
    const showHeader = !sameAuthor || !closeInTime || brokenBySeparator;

    rows.push({
      kind: 'message',
      key: message.id,
      message,
      isMine,
      showHeader,
    });
    previous = message;
  });

  // Closing a block is decided looking backwards, once every row exists.
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i].kind !== 'message') continue;
    const next = rows[i + 1];
    rows[i].endsGroup = !next || next.kind !== 'message' || next.showHeader;
  }
  return rows;
}

/** How many of these the viewer has not seen. Drives the "N new" pill. */
export function unreadCount(messages, { lastSeen = '', currentUserId = '' } = {}) {
  const seenAt = lastSeen ? time(lastSeen) : null;
  if (!seenAt) return 0;
  return (messages || []).filter((message) => {
    if (!message || message.authorId === currentUserId) return false;
    const at = time(message.createdAt);
    return !!at && at.getTime() > seenAt.getTime();
  }).length;
}
